// PortfolioScreen.js — Merged: Work History · Skills · Certifications · Education · Projects · Analytics
// 6 tabs, full CRUD on all sections, filters, search, sort, realtime events, analytics with charts

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, FlatList, TouchableOpacity, TextInput,
  Modal, KeyboardAvoidingView, Platform, Alert, ActivityIndicator, RefreshControl,
  Animated, SafeAreaView, StatusBar, Dimensions, Image,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import portfolioDatabase from '../../services/PortfolioDatabase';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// ─── Icons ────────────────────────────────────────────────────────────────────
let Ionicons, MaterialCommunityIcons;
try {
  const iv = require('@expo/vector-icons');
  Ionicons = iv.Ionicons;
  MaterialCommunityIcons = iv.MaterialCommunityIcons;
} catch {
  const F = ({ size = 20, style }) => (
    <View style={[{ width: size, height: size, borderRadius: size / 2, backgroundColor: '#94a3b8' }, style]} />
  );
  Ionicons = MaterialCommunityIcons = F;
}

// ─── LinearGradient ───────────────────────────────────────────────────────────
let LinearGradient;
try { LinearGradient = require('expo-linear-gradient').LinearGradient; } catch {
  LinearGradient = ({ colors, style, children }) => (
    <View style={[style, { backgroundColor: colors?.[0] || '#0e4e96' }]}>{children}</View>
  );
}

// ─── API ──────────────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');

// ─── Constants ────────────────────────────────────────────────────────────────
const SKILL_CATEGORIES  = ['Technical', 'Programming', 'Design', 'Management', 'Communication', 'Other'];
const SKILL_LEVELS_LIST = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
const EMP_TYPES         = ['Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship'];
const CERT_CATS         = ['Professional', 'Technical', 'Management', 'Safety', 'Academic', 'Other'];
const EDU_LEVELS        = ["High School", "Associate", "Bachelor's", "Master's", "PhD", "Certificate", "Diploma", "Professional"];
const EDU_STATUSES      = ['completed', 'in_progress', 'dropped_out'];
const PROJ_CATS         = ['Infrastructure', 'Energy', 'Healthcare', 'Transportation', 'Residential', 'Commercial', 'Technology', 'Other'];
const PROJ_STATUSES     = ['Planning', 'In Progress', 'Completed', 'On Hold', 'Cancelled'];

const mkId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;
const toStr = v => (v === null || v === undefined ? '' : String(v));

const TABS = [
  { key: 'work',   label: 'Work',    icon: 'briefcase-outline',       color: '#0e4e96' },
  { key: 'skills', label: 'Skills',  icon: 'star-outline',            color: '#8B5CF6' },
  { key: 'certs',  label: 'Certs',   icon: 'ribbon-outline',          color: '#D97706' },
  { key: 'edu',    label: 'Education', icon: 'school-outline',        color: '#059669' },
  { key: 'proj',   label: 'Projects', icon: 'code-slash-outline',     color: '#DB2777' },
  { key: 'analytics', label: 'Analytics', icon: 'analytics-outline',  color: '#667eea' },
];


// ─── Helper Functions ─────────────────────────────────────────────────────────
const getSkillLevelColor = p => p <= 25 ? '#94A3B8' : p <= 50 ? '#F59E0B' : p <= 75 ? '#0e4e96' : '#10B981';
const getSkillLevelLabel = p => p <= 25 ? 'Beginner' : p <= 50 ? 'Intermediate' : p <= 75 ? 'Advanced' : 'Expert';
const getProjStatusColor = s => ({ Planning:'#94A3B8', 'In Progress':'#0e4e96', Completed:'#10B981', 'On Hold':'#F59E0B', Cancelled:'#EF4444' }[s] || '#64748B');
const getEduStatusColor  = s => ({ completed:'#10B981', in_progress:'#3B82F6', dropped_out:'#EF4444' }[s] || '#64748B');
const getEduStatusLabel  = s => ({ completed:'Completed', in_progress:'In Progress', dropped_out:'Dropped Out' }[s] || 'Unknown');
const getEduLevelIcon    = l => ({ "High School":'school-outline', Associate:'library-outline', "Bachelor's":'school', "Master's":'medal-outline', PhD:'trophy-outline', Certificate:'ribbon-outline', Diploma:'document-text-outline', Professional:'briefcase-outline' }[l] || 'school-outline');

// ─── Main Component ───────────────────────────────────────────────────────────
export default function PortfolioScreen({ navigation: navProp, route }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const navHook = useNavigation();
  const navigation = navProp || navHook;

  // ── Tab ─────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(route?.params?.tab || 'work');

  // ── Global loading / refresh ─────────────────────────────────────────────────
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // ── Data ─────────────────────────────────────────────────────────────────────
  const [workHistory,    setWorkHistory]    = useState([]);
  const [skills,         setSkills]         = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [education,      setEducation]      = useState([]);
  const [projects,       setProjects]       = useState([]);
  const [analytics,      setAnalytics]      = useState({});
  const [submissionStats,setSubmissionStats]= useState({});

  // ── Filtered lists ───────────────────────────────────────────────────────────
  const [filteredWork,  setFilteredWork]  = useState([]);
  const [filteredSkills,setFilteredSkills]= useState([]);
  const [filteredCerts, setFilteredCerts] = useState([]);
  const [filteredEdu,   setFilteredEdu]   = useState([]);
  const [filteredProj,  setFilteredProj]  = useState([]);

  // ── Search + Filter state (per tab) ──────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [filterModal, setFilterModal] = useState(false);

  // Work filters
  const [workEmpType, setWorkEmpType] = useState('All');
  const [workStatus,  setWorkStatus]  = useState('All');
  const [workSort,    setWorkSort]    = useState('date');

  // Skills filters
  const [skillCategory, setSkillCategory] = useState('All');
  const [skillLevel,    setSkillLevel]    = useState('All');
  const [skillSort,     setSkillSort]     = useState('proficiency');

  // Certs filters
  const [certCategory, setCertCategory] = useState('All');
  const [certStatus,   setCertStatus]   = useState('All');
  const [certSort,     setCertSort]     = useState('date');

  // Edu filters
  const [eduLevel,  setEduLevel]  = useState('All');
  const [eduStatus, setEduStatus] = useState('All');
  const [eduSort,   setEduSort]   = useState('startDate');

  // Proj filters
  const [projCategory, setProjCategory] = useState('All');
  const [projStatus,   setProjStatus]   = useState('All');
  const [projSort,     setProjSort]     = useState('date');

  // Analytics
  const [analyticsPeriod, setAnalyticsPeriod] = useState('month');
  const [analyticsTab,    setAnalyticsTab]    = useState('overview');

  // ── Edit modal ───────────────────────────────────────────────────────────────
  const [editModal,  setEditModal]  = useState(false);
  const [editType,   setEditType]   = useState(null);
  const [editId,     setEditId]     = useState(null);
  const [formData,   setFormData]   = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [saving,     setSaving]     = useState(false);

  // ── Animation ─────────────────────────────────────────────────────────────────
  const fadeAnim = useRef(new Animated.Value(0)).current;

  // ── Init ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    initDb();
    Animated.timing(fadeAnim, { toValue: 1, duration: 500, useNativeDriver: true }).start();
  }, []);

  useFocusEffect(useCallback(() => { loadAll(); }, []));

  // Subscribe to portfolioDatabase events
  useEffect(() => {
    if (!portfolioDatabase.initialized) return;
    const unsubs = [
      portfolioDatabase.subscribe('skill_added',            s  => setSkills(p => [s, ...p])),
      portfolioDatabase.subscribe('skill_updated',          s  => setSkills(p => p.map(x => x.id===s.id?s:x))),
      portfolioDatabase.subscribe('skill_deleted',          ({id}) => setSkills(p => p.filter(x=>x.id!==id))),
      portfolioDatabase.subscribe('work_history_added',     w  => setWorkHistory(p => [w, ...p])),
      portfolioDatabase.subscribe('work_history_updated',   w  => setWorkHistory(p => p.map(x => x.id===w.id?w:x))),
      portfolioDatabase.subscribe('work_history_deleted',   ({id}) => setWorkHistory(p => p.filter(x=>x.id!==id))),
      portfolioDatabase.subscribe('certification_added',    c  => setCertifications(p => [c, ...p])),
      portfolioDatabase.subscribe('certification_updated',  c  => setCertifications(p => p.map(x => x.id===c.id?c:x))),
      portfolioDatabase.subscribe('certification_deleted',  ({id}) => setCertifications(p => p.filter(x=>x.id!==id))),
      portfolioDatabase.subscribe('project_added',          pr => setProjects(p => [pr, ...p])),
      portfolioDatabase.subscribe('project_updated',        pr => setProjects(p => p.map(x => x.id===pr.id?pr:x))),
      portfolioDatabase.subscribe('project_deleted',        ({id}) => setProjects(p => p.filter(x=>x.id!==id))),
    ];
    return () => unsubs.forEach(fn => typeof fn === 'function' && fn());
  }, [portfolioDatabase.initialized]);

  // ── Filters: recompute whenever data or filter params change ─────────────────
  useEffect(() => { applyWorkFilters(); },  [workHistory,  search, workEmpType, workStatus, workSort]);
  useEffect(() => { applySkillFilters(); }, [skills,       search, skillCategory, skillLevel, skillSort]);
  useEffect(() => { applyCertFilters(); },  [certifications, search, certCategory, certStatus, certSort]);
  useEffect(() => { applyEduFilters(); },   [education,    search, eduLevel, eduStatus, eduSort]);
  useEffect(() => { applyProjFilters(); },  [projects,     search, projCategory, projStatus, projSort]);

  // ── DB init + load all ────────────────────────────────────────────────────────
  const initDb = async () => {
    try {
      if (!portfolioDatabase.initialized) await portfolioDatabase.initialize();
    } catch (e) { console.warn('PortfolioScreen initDb:', e?.message); }
  };

  const loadAll = async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      if (!portfolioDatabase.initialized) await portfolioDatabase.initialize();
      const [wh, sk, ce, ed, pr] = await Promise.all([
        portfolioDatabase.getWorkHistory().catch(() => []),
        portfolioDatabase.getSkills().catch(() => []),
        portfolioDatabase.getCertifications().catch(() => []),
        portfolioDatabase.getEducation().catch(() => []),
        portfolioDatabase.getProjects().catch(() => []),
      ]);
      setWorkHistory(wh || []);
      setSkills(sk || []);
      setCertifications(ce || []);
      setEducation(ed || []);
      setProjects(pr || []);
      await loadAnalyticsData();
    } catch (e) {
      console.warn('PortfolioScreen loadAll:', e?.message);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadAll();
    setRefreshing(false);
  };

  // ── Analytics ─────────────────────────────────────────────────────────────────
  const loadAnalyticsData = async () => {
    try {
      const uid = await AsyncStorage.getItem('userId') || await AsyncStorage.getItem('user_id');
      if (uid) {
        const [ar, sr] = await Promise.all([
          apiFetch(`/api/professional/analytics?period=${analyticsPeriod}&user_id=${uid}`),
          apiFetch(`/api/data/applications?sort=created_at&order=desc&limit=100`),
        ]);
        if (!ar.error) setAnalytics(ar.data || ar);
        if (!sr.error) {
          const apps = Array.isArray(sr) ? sr : (sr.data || sr.results || []);
          // Compute local stats
          const total = apps.length;
          const interviews = apps.filter(a => a.status === 'interview').length;
          const offers     = apps.filter(a => a.status === 'offer').length;
          const byCompany  = {};
          apps.forEach(a => { byCompany[a.company||'Unknown'] = (byCompany[a.company||'Unknown']||0)+1; });
          const topCompanies = Object.entries(byCompany).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([company,count])=>({company,count}));
          setSubmissionStats({ total, interviews, offers, topCompanies, interviewRate: total?Math.round(interviews/total*100):0, offerRate: total?Math.round(offers/total*100):0 });
        }
      }
    } catch {}
  };

  useEffect(() => { loadAnalyticsData(); }, [analyticsPeriod]);


  // ─── Filter Functions ─────────────────────────────────────────────────────────
  const applyWorkFilters = () => {
    let f = [...workHistory];
    const q = search.toLowerCase();
    if (q) f = f.filter(w => [w.position,w.company,w.description,w.location].some(s => (s||'').toLowerCase().includes(q)));
    if (workEmpType !== 'All') f = f.filter(w => w.employmentType === workEmpType);
    if (workStatus  !== 'All') { const isCur = w => !w.endDate || w.endDate === 'Present'; f = f.filter(w => workStatus === 'Current' ? isCur(w) : !isCur(w)); }
    f.sort((a,b) => workSort === 'position' ? a.position.localeCompare(b.position) : workSort === 'company' ? a.company.localeCompare(b.company) : new Date(b.startDate) - new Date(a.startDate));
    setFilteredWork(f);
  };

  const applySkillFilters = () => {
    let f = [...skills];
    const q = search.toLowerCase();
    if (q) f = f.filter(s => [s.name,s.description,s.category].some(x => (x||'').toLowerCase().includes(q)));
    if (skillCategory !== 'All') f = f.filter(s => s.category === skillCategory);
    if (skillLevel !== 'All') {
      const ranges = { Beginner:[0,25], Intermediate:[26,50], Advanced:[51,75], Expert:[76,100] };
      const [mn,mx] = ranges[skillLevel] || [0,100];
      f = f.filter(s => s.proficiency >= mn && s.proficiency <= mx);
    }
    f.sort((a,b) => skillSort === 'name' ? a.name.localeCompare(b.name) : skillSort === 'category' ? a.category.localeCompare(b.category) : b.proficiency - a.proficiency);
    setFilteredSkills(f);
  };

  const applyCertFilters = () => {
    let f = [...certifications];
    const q = search.toLowerCase();
    if (q) f = f.filter(c => [c.title,c.issuer,c.description].some(x => (x||'').toLowerCase().includes(q)));
    if (certCategory !== 'All') f = f.filter(c => c.category === certCategory);
    f.sort((a,b) => certSort === 'title' ? (a.title||'').localeCompare(b.title||'') : (b.date||'').localeCompare(a.date||''));
    setFilteredCerts(f);
  };

  const applyEduFilters = () => {
    let f = [...education];
    const q = search.toLowerCase();
    if (q) f = f.filter(e => [e.degree,e.institution,e.fieldOfStudy,e.location].some(x => (x||'').toLowerCase().includes(q)));
    if (eduLevel  !== 'All') f = f.filter(e => e.level === eduLevel);
    if (eduStatus !== 'All') f = f.filter(e => e.status === eduStatus);
    f.sort((a,b) => eduSort === 'institution' ? (a.institution||'').localeCompare(b.institution||'') : new Date(b.startDate||0) - new Date(a.startDate||0));
    setFilteredEdu(f);
  };

  const applyProjFilters = () => {
    let f = [...projects];
    const q = search.toLowerCase();
    if (q) f = f.filter(p => [p.title,p.description,p.client].some(x => (x||'').toLowerCase().includes(q)));
    if (projCategory !== 'All') f = f.filter(p => p.category === projCategory);
    if (projStatus   !== 'All') f = f.filter(p => p.status   === projStatus);
    f.sort((a,b) => projSort === 'title' ? (a.title||'').localeCompare(b.title||'') : (b.date||'').localeCompare(a.date||''));
    setFilteredProj(f);
  };

  // ─── CRUD Handlers ────────────────────────────────────────────────────────────
  const openAdd = (type) => {
    const defaults = {
      work:  { company:'', position:'', startDate:'', endDate:'', description:'', location:'', employmentType:'Full-time', salary:'', skills:[] },
      skill: { name:'', proficiency:50, description:'', category:'Technical', yearsExperience:1 },
      cert:  { title:'', issuer:'', description:'', date:'', expiryDate:'', category:'Professional', credentialId:'', verificationUrl:'' },
      edu:   { degree:'', institution:'', fieldOfStudy:'', level:"Bachelor's", status:'completed', startDate:'', endDate:'', location:'', gpa:'', description:'' },
      proj:  { title:'', description:'', client:'', status:'Planning', date:'', budget:'', teamSize:'', category:'Infrastructure', technologies:[] },
    };
    setEditType(type); setEditId(null); setFormData(defaults[type] || {}); setFormErrors({}); setEditModal(true);
  };

  const openEdit = (type, item) => {
    setEditType(type); setEditId(item.id); setFormData({...item}); setFormErrors({}); setEditModal(true);
  };

  const confirmDelete = (type, item) => {
    const labels = { work:'work experience', skill:'skill', cert:'certification', edu:'education entry', proj:'project' };
    const names  = { work:`${item.position} at ${item.company}`, skill:item.name, cert:item.title, edu:`${item.degree} from ${item.institution}`, proj:item.title };
    Alert.alert(`Delete ${labels[type]}`, `Remove "${names[type]}"? This cannot be undone.`, [
      { text:'Cancel', style:'cancel' },
      { text:'Delete', style:'destructive', onPress: () => doDelete(type, item) },
    ]);
  };

  const doDelete = async (type, item) => {
    try {
      const fn = { work:()=>portfolioDatabase.deleteWorkHistory(item.id), skill:()=>portfolioDatabase.deleteSkill(item.id), cert:()=>portfolioDatabase.deleteCertification(item.id), edu:()=>portfolioDatabase.deleteEducation(item.id), proj:()=>portfolioDatabase.deleteProject(item.id) }[type];
      await fn();
      // Remove from local state immediately (events also fire)
      const setters = { work:setWorkHistory, skill:setSkills, cert:setCertifications, edu:setEducation, proj:setProjects };
      setters[type](prev => prev.filter(x => x.id !== item.id));
    } catch (e) { Alert.alert('Error', 'Could not delete item.'); }
  };

  const validateForm = (type, data) => {
    const e = {};
    if (type === 'work')  { if (!data.company?.trim()) e.company='Required'; if (!data.position?.trim()) e.position='Required'; if (!data.startDate?.trim()) e.startDate='Required'; if (!data.description?.trim()) e.description='Required'; }
    if (type === 'skill') { if (!data.name?.trim()) e.name='Required'; if (!data.proficiency||data.proficiency<1||data.proficiency>100) e.proficiency='1-100'; }
    if (type === 'cert')  { if (!data.title?.trim()) e.title='Required'; if (!data.issuer?.trim()) e.issuer='Required'; if (!data.date?.trim()) e.date='Required'; }
    if (type === 'edu')   { if (!data.degree?.trim()) e.degree='Required'; if (!data.institution?.trim()) e.institution='Required'; }
    if (type === 'proj')  { if (!data.title?.trim()) e.title='Required'; if (!data.description?.trim()) e.description='Required'; }
    return e;
  };

  const saveItem = async () => {
    const errs = validateForm(editType, formData);
    if (Object.keys(errs).length) { setFormErrors(errs); Alert.alert('Missing fields', 'Please fill in all required fields.'); return; }
    setSaving(true);
    try {
      const isEdit = !!editId;
      const db = portfolioDatabase;
      const fns = {
        work:  { add: () => db.addWorkHistory(formData),         upd: () => db.updateWorkHistory(editId, formData)  },
        skill: { add: () => db.addSkill(formData),               upd: () => db.updateSkill(editId, formData)        },
        cert:  { add: () => db.addCertification(formData),       upd: () => db.updateCertification(editId, formData)},
        edu:   { add: () => db.addEducation(formData),           upd: () => db.updateEducation(editId, formData)    },
        proj:  { add: () => db.addProject(formData),             upd: () => db.updateProject(editId, formData)      },
      };
      const result = isEdit ? await fns[editType].upd() : await fns[editType].add();
      if (result) {
        setEditModal(false);
        // Optimistically update local state
        if (isEdit) {
          const setters = { work:setWorkHistory, skill:setSkills, cert:setCertifications, edu:setEducation, proj:setProjects };
          setters[editType](prev => prev.map(x => x.id === editId ? { ...x, ...formData } : x));
        } else {
          const item = { id: result.id || mkId(), ...formData };
          const setters = { work:setWorkHistory, skill:setSkills, cert:setCertifications, edu:setEducation, proj:setProjects };
          setters[editType](prev => [item, ...prev]);
        }
      } else { Alert.alert('Error', 'Could not save. Please try again.'); }
    } catch (e) { Alert.alert('Error', e?.message || 'Save failed.'); }
    finally { setSaving(false); }
  };

  // ── Form field helper ─────────────────────────────────────────────────────────
  const F = ({ label, fkey, placeholder, multiline, keyboard, required }) => (
    <View style={s.ff}>
      <Text style={s.fl}>{label}{required && <Text style={{color:'#EF4444'}}> *</Text>}</Text>
      <TextInput
        style={[s.fi, multiline && s.fia, formErrors[fkey] && s.fierr]}
        value={toStr(formData[fkey])}
        onChangeText={v => setFormData(p => ({ ...p, [fkey]: v }))}
        placeholder={placeholder} placeholderTextColor="#94A3B8"
        multiline={!!multiline} numberOfLines={multiline ? 4 : 1}
        keyboardType={keyboard || 'default'}
        textAlignVertical={multiline ? 'top' : 'center'}
      />
      {formErrors[fkey] && <Text style={s.ferr}>{formErrors[fkey]}</Text>}
    </View>
  );

  const Chips = ({ options, selected, onSelect }) => (
    <View style={s.chips}>
      {options.map(o => (
        <TouchableOpacity key={o} style={[s.chip, selected===o && s.chipA]} onPress={() => onSelect(o)}>
          <Text style={[s.chipT, selected===o && s.chipTA]}>{o}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );


  // ─────────────────────────────────────────────────────────────────────────────
  // CARD RENDERS
  // ─────────────────────────────────────────────────────────────────────────────

  const WorkCard = ({ item: w }) => {
    const isCurrent = !w.endDate || w.endDate === 'Present';
    return (
      <View style={s.card}>
        <View style={s.cardRow}>
          <View style={[s.cardIcon, {backgroundColor:'#EFF6FF'}]}><Ionicons name="business-outline" size={22} color="#0e4e96"/></View>
          <View style={{flex:1}}>
            <Text style={s.cardTitle}>{toStr(w.position)}</Text>
            <Text style={s.cardSub}>{toStr(w.company)}</Text>
            <View style={s.cardMeta}>
              <View style={[s.badge, {backgroundColor: isCurrent?'#DCFCE7':' #F1F5F9'}]}>
                <Text style={[s.badgeT, {color: isCurrent?'#16A34A':'#64748B'}]}>{isCurrent?'Current':'Previous'}</Text>
              </View>
              {w.employmentType ? <View style={s.badge}><Text style={s.badgeT}>{w.employmentType}</Text></View> : null}
            </View>
          </View>
          <View style={s.cardActions}>
            <TouchableOpacity style={s.actBtn} onPress={() => openEdit('work', w)}><Ionicons name="create-outline" size={16} color="#0e4e96"/></TouchableOpacity>
            <TouchableOpacity style={[s.actBtn,{marginTop:4}]} onPress={() => confirmDelete('work', w)}><Ionicons name="trash-outline" size={16} color="#EF4444"/></TouchableOpacity>
          </View>
        </View>
        {w.description ? <Text style={s.cardDesc} numberOfLines={2}>{w.description}</Text> : null}
        <View style={s.cardFooter}>
          <Ionicons name="calendar-outline" size={13} color="#94A3B8"/><Text style={s.cardFooterT}>{w.startDate} – {w.endDate||'Present'}</Text>
          {w.location ? <><Ionicons name="location-outline" size={13} color="#94A3B8" style={{marginLeft:10}}/><Text style={s.cardFooterT}>{w.location}</Text></> : null}
          {w.salary   ? <><Ionicons name="cash-outline" size={13} color="#94A3B8" style={{marginLeft:10}}/><Text style={s.cardFooterT}>{w.salary}</Text></> : null}
        </View>
        {w.skills?.length > 0 && (
          <View style={s.tagRow}>
            {w.skills.slice(0,4).map((sk,i) => <View key={i} style={s.tag}><Text style={s.tagT}>{sk}</Text></View>)}
            {w.skills.length > 4 ? <View style={s.tag}><Text style={s.tagT}>+{w.skills.length-4}</Text></View> : null}
          </View>
        )}
      </View>
    );
  };

  const SkillCard = ({ item: sk }) => {
    const lc = getSkillLevelColor(sk.proficiency);
    return (
      <View style={s.card}>
        <View style={s.cardRow}>
          <View style={{flex:1}}>
            <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center'}}>
              <Text style={s.cardTitle}>{toStr(sk.name)}</Text>
              <Text style={[s.badge, {color:lc,fontWeight:'700',fontSize:11}]}>{getSkillLevelLabel(sk.proficiency)}</Text>
            </View>
            <Text style={s.cardSub}>{toStr(sk.category)}{sk.yearsExperience ? ` · ${sk.yearsExperience}yr` : ''}</Text>
          </View>
          <View style={s.cardActions}>
            <TouchableOpacity style={s.actBtn} onPress={() => openEdit('skill', sk)}><Ionicons name="create-outline" size={16} color="#0e4e96"/></TouchableOpacity>
            <TouchableOpacity style={[s.actBtn,{marginTop:4}]} onPress={() => confirmDelete('skill', sk)}><Ionicons name="trash-outline" size={16} color="#EF4444"/></TouchableOpacity>
          </View>
        </View>
        {sk.description ? <Text style={s.cardDesc} numberOfLines={2}>{sk.description}</Text> : null}
        <View style={s.progRow}>
          <View style={s.progTrack}><View style={[s.progFill, {width:`${sk.proficiency}%`, backgroundColor:lc}]}/></View>
          <Text style={[s.progLabel, {color:lc}]}>{sk.proficiency}%</Text>
        </View>
      </View>
    );
  };

  const CertCard = ({ item: c }) => (
    <View style={s.card}>
      <View style={s.cardRow}>
        <View style={[s.cardIcon, {backgroundColor:'#FFFBEB'}]}><Ionicons name="ribbon-outline" size={22} color="#D97706"/></View>
        <View style={{flex:1}}>
          <Text style={s.cardTitle}>{toStr(c.title)}</Text>
          <Text style={s.cardSub}>{toStr(c.issuer)}</Text>
          <View style={s.cardMeta}>
            {c.category ? <View style={s.badge}><Text style={s.badgeT}>{c.category}</Text></View> : null}
          </View>
        </View>
        <View style={s.cardActions}>
          <TouchableOpacity style={s.actBtn} onPress={() => openEdit('cert', c)}><Ionicons name="create-outline" size={16} color="#0e4e96"/></TouchableOpacity>
          <TouchableOpacity style={[s.actBtn,{marginTop:4}]} onPress={() => confirmDelete('cert', c)}><Ionicons name="trash-outline" size={16} color="#EF4444"/></TouchableOpacity>
        </View>
      </View>
      {c.description ? <Text style={s.cardDesc} numberOfLines={2}>{c.description}</Text> : null}
      <View style={s.cardFooter}>
        <Ionicons name="calendar-outline" size={13} color="#94A3B8"/><Text style={s.cardFooterT}>Issued: {c.date}</Text>
        {c.expiryDate ? <><Ionicons name="time-outline" size={13} color="#94A3B8" style={{marginLeft:10}}/><Text style={s.cardFooterT}>Exp: {c.expiryDate}</Text></> : null}
      </View>
      {c.credentialId ? <Text style={s.cardNote}>ID: {c.credentialId}</Text> : null}
    </View>
  );

  const EduCard = ({ item: e }) => (
    <View style={s.card}>
      <View style={s.cardRow}>
        <View style={[s.cardIcon, {backgroundColor:'#F0FDF4'}]}><Ionicons name={getEduLevelIcon(e.level)} size={22} color="#059669"/></View>
        <View style={{flex:1}}>
          <Text style={s.cardTitle}>{toStr(e.degree)}</Text>
          <Text style={s.cardSub}>{toStr(e.institution)}</Text>
          {e.fieldOfStudy ? <Text style={[s.cardNote,{marginTop:2}]}>{e.fieldOfStudy}</Text> : null}
          <View style={s.cardMeta}>
            <View style={[s.badge, {backgroundColor: getEduStatusColor(e.status)+'20'}]}>
              <Text style={[s.badgeT, {color: getEduStatusColor(e.status)}]}>{getEduStatusLabel(e.status)}</Text>
            </View>
            {e.level ? <View style={s.badge}><Text style={s.badgeT}>{e.level}</Text></View> : null}
          </View>
        </View>
        <View style={s.cardActions}>
          <TouchableOpacity style={s.actBtn} onPress={() => openEdit('edu', e)}><Ionicons name="create-outline" size={16} color="#0e4e96"/></TouchableOpacity>
          <TouchableOpacity style={[s.actBtn,{marginTop:4}]} onPress={() => confirmDelete('edu', e)}><Ionicons name="trash-outline" size={16} color="#EF4444"/></TouchableOpacity>
        </View>
      </View>
      {e.description ? <Text style={s.cardDesc} numberOfLines={2}>{e.description}</Text> : null}
      <View style={s.cardFooter}>
        {e.startDate ? <><Ionicons name="calendar-outline" size={13} color="#94A3B8"/><Text style={s.cardFooterT}>{e.startDate} – {e.endDate||'Present'}</Text></> : null}
        {e.location  ? <><Ionicons name="location-outline" size={13} color="#94A3B8" style={{marginLeft:8}}/><Text style={s.cardFooterT}>{e.location}</Text></> : null}
        {e.gpa       ? <><Ionicons name="school-outline"   size={13} color="#94A3B8" style={{marginLeft:8}}/><Text style={s.cardFooterT}>GPA: {e.gpa}</Text></> : null}
      </View>
      {e.achievements?.length > 0 && (
        <View style={{marginTop:8}}>
          {e.achievements.slice(0,2).map((a,i) => <Text key={i} style={s.cardNote}>• {a}</Text>)}
        </View>
      )}
    </View>
  );

  const ProjCard = ({ item: p }) => (
    <View style={s.card}>
      <View style={s.cardRow}>
        <View style={[s.cardIcon, {backgroundColor:'#F5F3FF'}]}><Ionicons name="code-slash-outline" size={22} color="#7C3AED"/></View>
        <View style={{flex:1}}>
          <Text style={s.cardTitle}>{toStr(p.title)}</Text>
          <Text style={s.cardSub}>{toStr(p.client)}</Text>
          <View style={s.cardMeta}>
            <View style={[s.badge, {backgroundColor: getProjStatusColor(p.status)+'20'}]}>
              <Text style={[s.badgeT, {color: getProjStatusColor(p.status)}]}>{p.status}</Text>
            </View>
            {p.category ? <View style={s.badge}><Text style={s.badgeT}>{p.category}</Text></View> : null}
          </View>
        </View>
        <View style={s.cardActions}>
          <TouchableOpacity style={s.actBtn} onPress={() => openEdit('proj', p)}><Ionicons name="create-outline" size={16} color="#0e4e96"/></TouchableOpacity>
          <TouchableOpacity style={[s.actBtn,{marginTop:4}]} onPress={() => confirmDelete('proj', p)}><Ionicons name="trash-outline" size={16} color="#EF4444"/></TouchableOpacity>
        </View>
      </View>
      {p.description ? <Text style={s.cardDesc} numberOfLines={2}>{p.description}</Text> : null}
      <View style={s.cardFooter}>
        {p.date     ? <><Ionicons name="calendar-outline" size={13} color="#94A3B8"/><Text style={s.cardFooterT}>{p.date}</Text></> : null}
        {p.budget   ? <><Ionicons name="cash-outline" size={13} color="#94A3B8" style={{marginLeft:10}}/><Text style={s.cardFooterT}>{p.budget}</Text></> : null}
        {p.teamSize ? <><Ionicons name="people-outline" size={13} color="#94A3B8" style={{marginLeft:10}}/><Text style={s.cardFooterT}>{p.teamSize}</Text></> : null}
      </View>
      {p.technologies?.length > 0 && (
        <View style={s.tagRow}>
          {p.technologies.slice(0,4).map((t,i) => <View key={i} style={[s.tag,{backgroundColor:'#F5F3FF',borderColor:'#DDD6FE'}]}><Text style={[s.tagT,{color:'#7C3AED'}]}>{t}</Text></View>)}
          {p.technologies.length > 4 ? <View style={s.tag}><Text style={s.tagT}>+{p.technologies.length-4}</Text></View> : null}
        </View>
      )}
    </View>
  );

  // ─── Empty state ──────────────────────────────────────────────────────────────
  const Empty = ({ icon, title, sub, onAdd, addLabel }) => (
    <View style={s.empty}>
      <Ionicons name={icon} size={56} color="#CBD5E1"/>
      <Text style={s.emptyT}>{title}</Text>
      <Text style={s.emptyS}>{sub}</Text>
      {onAdd && <TouchableOpacity style={s.emptyBtn} onPress={onAdd}><Text style={s.emptyBtnT}>{addLabel||'Add Now'}</Text></TouchableOpacity>}
    </View>
  );


  // ─── Summary stats bar ────────────────────────────────────────────────────────
  const StatsBar = () => (
    <View style={s.statsBar}>
      {[
        { label:'Work',    count:workHistory.length,    color:'#0e4e96', tab:'work'   },
        { label:'Skills',  count:skills.length,         color:'#8B5CF6', tab:'skills' },
        { label:'Certs',   count:certifications.length, color:'#D97706', tab:'certs'  },
        { label:'Edu',     count:education.length,      color:'#059669', tab:'edu'    },
        { label:'Projects',count:projects.length,       color:'#DB2777', tab:'proj'   },
      ].map(item => (
        <TouchableOpacity key={item.tab} style={s.statItem} onPress={() => setActiveTab(item.tab)}>
          <Text style={[s.statNum, {color:item.color}]}>{item.count}</Text>
          <Text style={s.statLbl}>{item.label}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  // ─── Search + filter bar ──────────────────────────────────────────────────────
  const SearchBar = ({ placeholder, onAdd }) => (
    <View style={s.searchWrap}>
      <View style={s.searchBox}>
        <Ionicons name="search-outline" size={18} color="#94A3B8"/>
        <TextInput style={s.searchIn} placeholder={placeholder||'Search...'} value={search} onChangeText={setSearch} placeholderTextColor="#94A3B8"/>
        {search ? <TouchableOpacity onPress={() => setSearch('')}><Ionicons name="close-circle" size={18} color="#94A3B8"/></TouchableOpacity> : null}
      </View>
      <TouchableOpacity style={s.filterBtn} onPress={() => setFilterModal(true)}>
        <Ionicons name="filter-outline" size={18} color="#0e4e96"/>
      </TouchableOpacity>
      <TouchableOpacity style={s.addBtn} onPress={onAdd}>
        <Ionicons name="add" size={20} color="#FFF"/>
      </TouchableOpacity>
    </View>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // TAB CONTENT RENDERS
  // ─────────────────────────────────────────────────────────────────────────────

  const renderWork = () => (
    <Animated.View style={{opacity:fadeAnim}}>
      <SearchBar placeholder="Search work history..." onAdd={() => openAdd('work')} />
      <FlatList
        data={filteredWork}
        renderItem={WorkCard}
        keyExtractor={i => i.id}
        contentContainerStyle={s.list}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']}/>}
        ListEmptyComponent={<Empty icon="briefcase-outline" title="No work history" sub={search?'Try adjusting your search':'Add your first work experience'} onAdd={() => openAdd('work')} addLabel="Add Experience"/>}
      />
    </Animated.View>
  );

  const renderSkills = () => (
    <Animated.View style={{opacity:fadeAnim}}>
      <SearchBar placeholder="Search skills..." onAdd={() => openAdd('skill')} />
      <FlatList
        data={filteredSkills}
        renderItem={SkillCard}
        keyExtractor={i => i.id || i.name}
        contentContainerStyle={s.list}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#8B5CF6']}/>}
        ListEmptyComponent={<Empty icon="star-outline" title="No skills" sub={search?'Try adjusting your search':'Add your first skill'} onAdd={() => openAdd('skill')} addLabel="Add Skill"/>}
      />
    </Animated.View>
  );

  const renderCerts = () => (
    <Animated.View style={{opacity:fadeAnim}}>
      <SearchBar placeholder="Search certifications..." onAdd={() => openAdd('cert')} />
      <FlatList
        data={filteredCerts}
        renderItem={CertCard}
        keyExtractor={i => i.id}
        contentContainerStyle={s.list}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#D97706']}/>}
        ListEmptyComponent={<Empty icon="ribbon-outline" title="No certifications" sub={search?'Try adjusting your search':'Add your first certification'} onAdd={() => openAdd('cert')} addLabel="Add Certification"/>}
      />
    </Animated.View>
  );

  const renderEdu = () => (
    <Animated.View style={{opacity:fadeAnim}}>
      <SearchBar placeholder="Search education..." onAdd={() => openAdd('edu')} />
      <FlatList
        data={filteredEdu}
        renderItem={EduCard}
        keyExtractor={i => i.id}
        contentContainerStyle={s.list}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#059669']}/>}
        ListEmptyComponent={<Empty icon="school-outline" title="No education entries" sub={search?'Try adjusting your search':'Add your first degree'} onAdd={() => openAdd('edu')} addLabel="Add Education"/>}
      />
    </Animated.View>
  );

  const renderProj = () => (
    <Animated.View style={{opacity:fadeAnim}}>
      <SearchBar placeholder="Search projects..." onAdd={() => openAdd('proj')} />
      <FlatList
        data={filteredProj}
        renderItem={ProjCard}
        keyExtractor={i => i.id}
        contentContainerStyle={s.list}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#DB2777']}/>}
        ListEmptyComponent={<Empty icon="code-slash-outline" title="No projects" sub={search?'Try adjusting your search':'Add your first project'} onAdd={() => openAdd('proj')} addLabel="Add Project"/>}
      />
    </Animated.View>
  );

  // ─── Analytics tab ────────────────────────────────────────────────────────────
  const renderAnalytics = () => {
    const periods = [
      {key:'week',label:'Week'},{key:'month',label:'Month'},{key:'quarter',label:'Quarter'},{key:'year',label:'Year'},
    ];
    const atabs = [
      {key:'overview',label:'Overview',icon:'analytics-outline'},
      {key:'trends',  label:'Trends',  icon:'trending-up-outline'},
      {key:'insights',label:'Insights',icon:'bulb-outline'},
    ];
    const total = submissionStats.total || 0;
    const ir    = submissionStats.interviewRate || 0;
    const ofr   = submissionStats.offerRate || 0;
    const sr    = total && submissionStats.interviews ? Math.round(submissionStats.interviews/total*100) : 0;

    const metrics = [
      { label:'Applications', value:total,         icon:'paper-plane-outline', colors:['#667eea','#764ba2'] },
      { label:'Interview Rate',value:`${ir}%`,      icon:'calendar-outline',   colors:['#4facfe','#00f2fe'] },
      { label:'Offer Rate',    value:`${ofr}%`,     icon:'gift-outline',       colors:['#f093fb','#f5576c'] },
      { label:'Skills',        value:skills.length, icon:'star-outline',       colors:['#43e97b','#38f9d7'] },
    ];

    return (
      <ScrollView style={{flex:1}} contentContainerStyle={{paddingBottom:40}} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh}/>}>
        {/* Period selector */}
        <View style={s.periodRow}>
          {periods.map(p => (
            <TouchableOpacity key={p.key} style={[s.periodBtn, analyticsPeriod===p.key && s.periodBtnA]} onPress={() => setAnalyticsPeriod(p.key)}>
              <Text style={[s.periodBtnT, analyticsPeriod===p.key && s.periodBtnTA]}>{p.label}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Analytics sub-tabs */}
        <View style={s.aTabRow}>
          {atabs.map(t => (
            <TouchableOpacity key={t.key} style={[s.aTab, analyticsTab===t.key && s.aTabA]} onPress={() => setAnalyticsTab(t.key)}>
              <Ionicons name={t.icon} size={16} color={analyticsTab===t.key?'#667eea':'#94A3B8'}/>
              <Text style={[s.aTabT, analyticsTab===t.key && s.aTabTA]}>{t.label}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {analyticsTab === 'overview' && (
          <View>
            {/* Key metrics 2x2 grid */}
            <View style={s.metricsGrid}>
              {metrics.map((m,i) => (
                <LinearGradient key={i} colors={m.colors} start={{x:0,y:0}} end={{x:1,y:1}} style={s.metricCard}>
                  <Ionicons name={m.icon} size={26} color="#FFF"/>
                  <Text style={s.metricVal}>{m.value}</Text>
                  <Text style={s.metricLbl}>{m.label}</Text>
                </LinearGradient>
              ))}
            </View>

            {/* Portfolio summary */}
            <View style={s.aCard}>
              <Text style={s.aCardTitle}>Portfolio Summary</Text>
              {[
                {label:'Work History',   count:workHistory.length,    icon:'briefcase-outline',     color:'#0e4e96'},
                {label:'Skills',         count:skills.length,         icon:'star-outline',           color:'#8B5CF6'},
                {label:'Certifications', count:certifications.length, icon:'ribbon-outline',         color:'#D97706'},
                {label:'Education',      count:education.length,      icon:'school-outline',         color:'#059669'},
                {label:'Projects',       count:projects.length,       icon:'code-slash-outline',     color:'#DB2777'},
              ].map((item,i) => (
                <View key={i} style={s.summaryRow}>
                  <View style={[s.summaryIcon, {backgroundColor: item.color+'20'}]}>
                    <Ionicons name={item.icon} size={18} color={item.color}/>
                  </View>
                  <Text style={s.summaryLbl}>{item.label}</Text>
                  <View style={s.summaryBarWrap}>
                    <View style={s.summaryBarTrack}>
                      <View style={[s.summaryBarFill, {width:`${Math.min(100,(item.count/Math.max(workHistory.length,skills.length,certifications.length,education.length,projects.length,1))*100)}%`, backgroundColor:item.color}]}/>
                    </View>
                  </View>
                  <Text style={[s.summaryCount, {color:item.color}]}>{item.count}</Text>
                </View>
              ))}
            </View>

            {/* Top companies */}
            {submissionStats.topCompanies?.length > 0 && (
              <View style={s.aCard}>
                <Text style={s.aCardTitle}>Top Applied Companies</Text>
                {submissionStats.topCompanies.map((c,i) => (
                  <View key={i} style={s.companyRow}>
                    <View style={s.companyRank}><Text style={s.companyRankT}>{i+1}</Text></View>
                    <Text style={s.companyName}>{c.company}</Text>
                    <Text style={s.companyCount}>{c.count} apps</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        )}

        {analyticsTab === 'trends' && (
          <View>
            {/* Skill proficiency distribution */}
            <View style={s.aCard}>
              <Text style={s.aCardTitle}>Skill Proficiency Distribution</Text>
              {[
                {label:'Expert (76–100%)',       color:'#10B981', count:skills.filter(sk=>sk.proficiency>75).length},
                {label:'Advanced (51–75%)',       color:'#0e4e96', count:skills.filter(sk=>sk.proficiency>50&&sk.proficiency<=75).length},
                {label:'Intermediate (26–50%)',   color:'#F59E0B', count:skills.filter(sk=>sk.proficiency>25&&sk.proficiency<=50).length},
                {label:'Beginner (0–25%)',         color:'#94A3B8', count:skills.filter(sk=>sk.proficiency<=25).length},
              ].map((item,i) => (
                <View key={i} style={s.distRow}>
                  <View style={[s.distDot,{backgroundColor:item.color}]}/>
                  <Text style={s.distLbl}>{item.label}</Text>
                  <View style={s.distBarTrack}><View style={[s.distBarFill,{width:`${skills.length?Math.round(item.count/skills.length*100):0}%`,backgroundColor:item.color}]}/></View>
                  <Text style={[s.distCount,{color:item.color}]}>{item.count}</Text>
                </View>
              ))}
            </View>

            {/* Employment types */}
            <View style={s.aCard}>
              <Text style={s.aCardTitle}>Employment Types</Text>
              {EMP_TYPES.map((type,i) => {
                const count = workHistory.filter(w => w.employmentType === type).length;
                if (!count) return null;
                return (
                  <View key={i} style={s.distRow}>
                    <View style={[s.distDot,{backgroundColor:'#0e4e96'}]}/>
                    <Text style={s.distLbl}>{type}</Text>
                    <View style={s.distBarTrack}><View style={[s.distBarFill,{width:`${workHistory.length?Math.round(count/workHistory.length*100):0}%`,backgroundColor:'#0e4e96'}]}/></View>
                    <Text style={[s.distCount,{color:'#0e4e96'}]}>{count}</Text>
                  </View>
                );
              }).filter(Boolean)}
            </View>

            {/* Certification categories */}
            {certifications.length > 0 && (
              <View style={s.aCard}>
                <Text style={s.aCardTitle}>Certification Categories</Text>
                {CERT_CATS.map((cat,i) => {
                  const count = certifications.filter(c => c.category === cat).length;
                  if (!count) return null;
                  return (
                    <View key={i} style={s.distRow}>
                      <View style={[s.distDot,{backgroundColor:'#D97706'}]}/>
                      <Text style={s.distLbl}>{cat}</Text>
                      <View style={s.distBarTrack}><View style={[s.distBarFill,{width:`${certifications.length?Math.round(count/certifications.length*100):0}%`,backgroundColor:'#D97706'}]}/></View>
                      <Text style={[s.distCount,{color:'#D97706'}]}>{count}</Text>
                    </View>
                  );
                }).filter(Boolean)}
              </View>
            )}
          </View>
        )}

        {analyticsTab === 'insights' && (
          <View>
            <LinearGradient colors={['#667eea','#764ba2']} start={{x:0,y:0}} end={{x:1,y:1}} style={[s.aCard,{borderWidth:0}]}>
              <Text style={[s.aCardTitle,{color:'#FFF'}]}>🤖 AI Insights</Text>
              {[
                { emoji:'🎯', title:'Application Strategy', text:`Your portfolio has ${workHistory.length} work entries. Profiles with 3+ experiences get 60% more recruiter views.` },
                { emoji:'📈', title:'Skill Coverage', text:`You have ${skills.length} skills listed. Adding 2–3 more industry keywords could improve ATS matching by up to 25%.` },
                { emoji:'💼', title:'Profile Completeness', text:`${[workHistory,skills,certifications,education,projects].filter(a=>a.length>0).length}/5 portfolio sections filled. Complete all sections for maximum visibility.` },
                { emoji:'🏆', title:'Certifications', text:certifications.length > 0 ? `Great! ${certifications.length} certification${certifications.length>1?'s':''} adds strong credibility to your profile.` : 'Adding certifications increases trust scores. Consider adding your professional qualifications.' },
              ].map((item,i) => (
                <View key={i} style={s.insightItem}>
                  <Text style={s.insightEmoji}>{item.emoji}</Text>
                  <View style={{flex:1}}>
                    <Text style={[s.insightTitle,{color:'#FFF'}]}>{item.title}</Text>
                    <Text style={[s.insightText,{color:'rgba(255,255,255,0.85)'}]}>{item.text}</Text>
                  </View>
                </View>
              ))}
            </LinearGradient>

            <View style={s.aCard}>
              <Text style={s.aCardTitle}>Recommendations</Text>
              {[
                { icon:'school-outline',      label:'Complete Education',    text:'Add all your degrees and qualifications',     color:'#059669', action:() => setActiveTab('edu') },
                { icon:'star-outline',        label:'Add More Skills',       text:'Aim for 10+ skills covering technical & soft', color:'#8B5CF6', action:() => openAdd('skill') },
                { icon:'document-text-outline',label:'Update Resume',        text:'Sync your portfolio to your resume',          color:'#0e4e96', action:() => navigation.navigate('BuildResume') },
                { icon:'ribbon-outline',      label:'Get Certified',         text:'Add industry certifications to stand out',     color:'#D97706', action:() => openAdd('cert') },
              ].map((r,i) => (
                <TouchableOpacity key={i} style={s.recRow} onPress={r.action}>
                  <View style={[s.recIcon,{backgroundColor:r.color+'20'}]}><Ionicons name={r.icon} size={18} color={r.color}/></View>
                  <View style={{flex:1}}>
                    <Text style={s.recTitle}>{r.label}</Text>
                    <Text style={s.recText}>{r.text}</Text>
                  </View>
                  <Ionicons name="chevron-forward-outline" size={16} color="#CBD5E1"/>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        )}
      </ScrollView>
    );
  };


  // ─── Filter Modal ─────────────────────────────────────────────────────────────
  const renderFilterModal = () => {
    const clearAll = () => {
      setWorkEmpType('All'); setWorkStatus('All'); setWorkSort('date');
      setSkillCategory('All'); setSkillLevel('All'); setSkillSort('proficiency');
      setCertCategory('All'); setCertStatus('All'); setCertSort('date');
      setEduLevel('All'); setEduStatus('All'); setEduSort('startDate');
      setProjCategory('All'); setProjStatus('All'); setProjSort('date');
    };
    const Sec = ({ title }) => <Text style={s.fsecT}>{title}</Text>;
    return (
      <Modal visible={filterModal} transparent animationType="slide" onRequestClose={() => setFilterModal(false)}>
        <View style={s.mOverlay}>
          <View style={s.mSheet}>
            <View style={s.mHdr}>
              <Text style={s.mTitle}>Filters & Sort</Text>
              <TouchableOpacity onPress={() => setFilterModal(false)}><Ionicons name="close" size={22} color="#64748B"/></TouchableOpacity>
            </View>
            <ScrollView contentContainerStyle={{padding:16,paddingBottom:20}}>

              {activeTab === 'work' && (<>
                <Sec title="Employment Type"/>
                <Chips options={['All',...EMP_TYPES]} selected={workEmpType} onSelect={setWorkEmpType}/>
                <Sec title="Status"/>
                <Chips options={['All','Current','Previous']} selected={workStatus} onSelect={setWorkStatus}/>
                <Sec title="Sort By"/>
                <Chips options={['date','position','company']} selected={workSort} onSelect={setWorkSort}/>
              </>)}

              {activeTab === 'skills' && (<>
                <Sec title="Category"/>
                <Chips options={['All',...SKILL_CATEGORIES]} selected={skillCategory} onSelect={setSkillCategory}/>
                <Sec title="Level"/>
                <Chips options={['All',...SKILL_LEVELS_LIST]} selected={skillLevel} onSelect={setSkillLevel}/>
                <Sec title="Sort By"/>
                <Chips options={['proficiency','name','category','experience']} selected={skillSort} onSelect={setSkillSort}/>
              </>)}

              {activeTab === 'certs' && (<>
                <Sec title="Category"/>
                <Chips options={['All',...CERT_CATS]} selected={certCategory} onSelect={setCertCategory}/>
                <Sec title="Sort By"/>
                <Chips options={['date','title']} selected={certSort} onSelect={setCertSort}/>
              </>)}

              {activeTab === 'edu' && (<>
                <Sec title="Level"/>
                <Chips options={['All',...EDU_LEVELS]} selected={eduLevel} onSelect={setEduLevel}/>
                <Sec title="Status"/>
                <Chips options={['All',...EDU_STATUSES]} selected={eduStatus} onSelect={setEduStatus}/>
                <Sec title="Sort By"/>
                <Chips options={['startDate','institution','degree','level']} selected={eduSort} onSelect={setEduSort}/>
              </>)}

              {activeTab === 'proj' && (<>
                <Sec title="Category"/>
                <Chips options={['All',...PROJ_CATS]} selected={projCategory} onSelect={setProjCategory}/>
                <Sec title="Status"/>
                <Chips options={['All',...PROJ_STATUSES]} selected={projStatus} onSelect={setProjStatus}/>
                <Sec title="Sort By"/>
                <Chips options={['date','title','client','status']} selected={projSort} onSelect={setProjSort}/>
              </>)}

            </ScrollView>
            <View style={s.mFooter}>
              <TouchableOpacity style={s.mGhost} onPress={clearAll}><Text style={s.mGhostT}>Clear All</Text></TouchableOpacity>
              <TouchableOpacity style={s.mPrimary} onPress={() => setFilterModal(false)}><Text style={s.mPrimaryT}>Apply</Text></TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  // ─── Edit / Add Modal ─────────────────────────────────────────────────────────
  const renderEditModal = () => {
    const isEdit = !!editId;
    const titles = { work:'Work Experience', skill:'Skill', cert:'Certification', edu:'Education', proj:'Project' };

    return (
      <Modal visible={editModal} transparent animationType="slide" onRequestClose={() => setEditModal(false)}>
        <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'} style={s.mOverlay}>
          <View style={[s.mSheet,{maxHeight:height*0.92}]}>
            <View style={s.mHdr}>
              <Text style={s.mTitle}>{isEdit?'Edit':'Add'} {titles[editType]||''}</Text>
              <View style={{flexDirection:'row',gap:8,alignItems:'center'}}>
                {saving && <ActivityIndicator size={18} color="#0e4e96"/>}
                <TouchableOpacity style={s.saveBtnSm} onPress={saveItem} disabled={saving}>
                  <Text style={s.saveBtnSmT}>Save</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setEditModal(false)}><Ionicons name="close" size={22} color="#64748B"/></TouchableOpacity>
              </View>
            </View>

            <ScrollView contentContainerStyle={{padding:16,paddingBottom:24}}>
              {editType === 'work' && (<>
                <F label="Company"        fkey="company"        placeholder="Company name"              required/>
                <F label="Position"       fkey="position"       placeholder="Job title / position"      required/>
                <F label="Description"    fkey="description"    placeholder="Roles and responsibilities" multiline required/>
                <F label="Location"       fkey="location"       placeholder="City, Country"/>
                <View style={{flexDirection:'row',gap:12}}>
                  <View style={{flex:1}}><F label="Start Date" fkey="startDate" placeholder="Jan 2022" required/></View>
                  <View style={{flex:1}}><F label="End Date"   fkey="endDate"   placeholder="Present"/></View>
                </View>
                <View style={s.ff}>
                  <Text style={s.fl}>Employment Type</Text>
                  <Chips options={EMP_TYPES} selected={formData.employmentType||'Full-time'} onSelect={v => setFormData(p=>({...p,employmentType:v}))}/>
                </View>
                <F label="Salary (optional)"   fkey="salary"   placeholder="e.g. £50,000/yr"/>
              </>)}

              {editType === 'skill' && (<>
                <F label="Skill Name" fkey="name" placeholder="e.g. AutoCAD, Python, HVAC" required/>
                <View style={s.ff}>
                  <Text style={s.fl}>Proficiency: {formData.proficiency||50}% · {getSkillLevelLabel(formData.proficiency||50)}</Text>
                  <View style={s.profWrap}>
                    <View style={s.profTrack}><View style={[s.profFill,{width:`${formData.proficiency||50}%`,backgroundColor:getSkillLevelColor(formData.proficiency||50)}]}/></View>
                    <View style={s.profBtns}>
                      {[1,5,10,25].map(step => (
                        <TouchableOpacity key={`-${step}`} style={s.profBtn} onPress={() => setFormData(p=>({...p,proficiency:Math.max(0,(p.proficiency||50)-step)}))}><Text style={s.profBtnT}>-{step}</Text></TouchableOpacity>
                      ))}
                      {[1,5,10,25].map(step => (
                        <TouchableOpacity key={`+${step}`} style={[s.profBtn,{backgroundColor:'#EFF6FF',borderColor:'#BFDBFE'}]} onPress={() => setFormData(p=>({...p,proficiency:Math.min(100,(p.proficiency||50)+step)}))}><Text style={[s.profBtnT,{color:'#0e4e96'}]}>+{step}</Text></TouchableOpacity>
                      ))}
                    </View>
                  </View>
                </View>
                <View style={s.ff}>
                  <Text style={s.fl}>Category</Text>
                  <Chips options={SKILL_CATEGORIES} selected={formData.category||'Technical'} onSelect={v => setFormData(p=>({...p,category:v}))}/>
                </View>
                <F label="Description" fkey="description" placeholder="Describe your experience with this skill" multiline required/>
                <F label="Years of Experience" fkey="yearsExperience" placeholder="e.g. 3" keyboard="numeric"/>
              </>)}

              {editType === 'cert' && (<>
                <F label="Certificate Title" fkey="title"  placeholder="e.g. NEBOSH General Certificate" required/>
                <F label="Issuer"            fkey="issuer" placeholder="Issuing organisation"             required/>
                <F label="Description"       fkey="description" placeholder="What it covers and its relevance" multiline/>
                <View style={{flexDirection:'row',gap:12}}>
                  <View style={{flex:1}}><F label="Issue Date" fkey="date"       placeholder="June 2023" required/></View>
                  <View style={{flex:1}}><F label="Expiry"     fkey="expiryDate" placeholder="No Expiry"/></View>
                </View>
                <View style={s.ff}>
                  <Text style={s.fl}>Category</Text>
                  <Chips options={CERT_CATS} selected={formData.category||'Professional'} onSelect={v => setFormData(p=>({...p,category:v}))}/>
                </View>
                <F label="Credential ID"     fkey="credentialId"    placeholder="ABC-123456"/>
                <F label="Verification URL"  fkey="verificationUrl" placeholder="https://verify.example.com"/>
              </>)}

              {editType === 'edu' && (<>
                <F label="Degree / Qualification" fkey="degree"        placeholder="e.g. BEng Mechanical Engineering" required/>
                <F label="Institution"             fkey="institution"   placeholder="e.g. University of Manchester"     required/>
                <F label="Field of Study"          fkey="fieldOfStudy"  placeholder="e.g. Mechanical Engineering"/>
                <View style={s.ff}>
                  <Text style={s.fl}>Level</Text>
                  <Chips options={EDU_LEVELS} selected={formData.level||"Bachelor's"} onSelect={v => setFormData(p=>({...p,level:v}))}/>
                </View>
                <View style={s.ff}>
                  <Text style={s.fl}>Status</Text>
                  <Chips options={EDU_STATUSES} selected={formData.status||'completed'} onSelect={v => setFormData(p=>({...p,status:v}))}/>
                </View>
                <View style={{flexDirection:'row',gap:12}}>
                  <View style={{flex:1}}><F label="Start" fkey="startDate" placeholder="2018"/></View>
                  <View style={{flex:1}}><F label="End"   fkey="endDate"   placeholder="2022"/></View>
                </View>
                <F label="Location"    fkey="location"    placeholder="Manchester, UK"/>
                <F label="GPA / Grade" fkey="gpa"         placeholder="e.g. 3.8 / First Class"/>
                <F label="Description" fkey="description" placeholder="Relevant modules, thesis topic…" multiline/>
              </>)}

              {editType === 'proj' && (<>
                <F label="Project Title" fkey="title"       placeholder="e.g. Smart Energy Monitor"      required/>
                <F label="Description"   fkey="description" placeholder="Describe the project and your role" multiline required/>
                <F label="Client"        fkey="client"      placeholder="Client or organisation name"/>
                <F label="Date Range"    fkey="date"        placeholder="Jan 2024 – Mar 2024"/>
                <View style={s.ff}>
                  <Text style={s.fl}>Status</Text>
                  <Chips options={PROJ_STATUSES} selected={formData.status||'Planning'} onSelect={v => setFormData(p=>({...p,status:v}))}/>
                </View>
                <View style={s.ff}>
                  <Text style={s.fl}>Category</Text>
                  <Chips options={PROJ_CATS} selected={formData.category||'Infrastructure'} onSelect={v => setFormData(p=>({...p,category:v}))}/>
                </View>
                <F label="Budget (optional)"    fkey="budget"   placeholder="e.g. £250,000"/>
                <F label="Team Size (optional)" fkey="teamSize" placeholder="e.g. 8 members"/>
              </>)}
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    );
  };

  // ─── Main render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <SafeAreaView style={s.root}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96"/>
        <View style={s.header}>
          <TouchableOpacity style={s.backBtn} onPress={() => navigation.goBack()}><Ionicons name="arrow-back" size={22} color="#FFF"/></TouchableOpacity>
          <Text style={s.headerT}>Portfolio</Text>
          <View style={{width:36}}/>
        </View>
        <View style={s.loadWrap}><ActivityIndicator size={40} color="#0e4e96"/><Text style={s.loadT}>Loading portfolio…</Text></View>
      </SafeAreaView>
    );
  }

  const tabContent = () => {
    if (activeTab === 'work')      return renderWork();
    if (activeTab === 'skills')    return renderSkills();
    if (activeTab === 'certs')     return renderCerts();
    if (activeTab === 'edu')       return renderEdu();
    if (activeTab === 'proj')      return renderProj();
    if (activeTab === 'analytics') return renderAnalytics();
    return renderWork();
  };

  const activeColor = TABS.find(t => t.key === activeTab)?.color || '#0e4e96';

  return (
    <SafeAreaView style={s.root}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96"/>

      {/* Header */}
      <View style={s.header}>
        <TouchableOpacity style={s.backBtn} onPress={() => navigation.goBack()}><Ionicons name="arrow-back" size={22} color="#FFF"/></TouchableOpacity>
        <Text style={s.headerT}>My Portfolio</Text>
        <TouchableOpacity style={s.headerBtn} onPress={() => openAdd(activeTab === 'analytics' ? 'skill' : activeTab)}>
          <Ionicons name="add" size={22} color="#FFF"/>
        </TouchableOpacity>
      </View>

      {/* Summary stats */}
      <StatsBar/>

      {/* Tab bar */}
      <View style={s.tabBar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.tabBarContent}>
          {TABS.map(t => (
            <TouchableOpacity key={t.key} style={[s.tabBtn, activeTab===t.key && {borderBottomColor:t.color, borderBottomWidth:3}]} onPress={() => setActiveTab(t.key)}>
              <Ionicons name={t.icon} size={16} color={activeTab===t.key ? t.color : '#94A3B8'}/>
              <Text style={[s.tabBtnT, activeTab===t.key && {color:t.color}]}>{t.label}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Content area */}
      <View style={{flex:1, backgroundColor:'#F8FAFC'}}>
        {tabContent()}
      </View>

      {renderFilterModal()}
      {renderEditModal()}
    </SafeAreaView>
  );
}


// ─── Styles ────────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  root:       { flex:1, backgroundColor:'#0e4e96' },

  // Header
  header:     { flexDirection:'row', alignItems:'center', paddingHorizontal:16, paddingVertical:12, backgroundColor:'#0e4e96' },
  backBtn:    { width:36, height:36, borderRadius:10, backgroundColor:'rgba(255,255,255,0.15)', justifyContent:'center', alignItems:'center' },
  headerT:    { flex:1, textAlign:'center', color:'#FFF', fontSize:18, fontWeight:'700' },
  headerBtn:  { width:36, height:36, borderRadius:10, backgroundColor:'rgba(255,255,255,0.15)', justifyContent:'center', alignItems:'center' },

  // Stats bar
  statsBar:   { flexDirection:'row', backgroundColor:'#0a3d7a', paddingVertical:10 },
  statItem:   { flex:1, alignItems:'center' },
  statNum:    { fontSize:18, fontWeight:'900' },
  statLbl:    { fontSize:10, color:'rgba(255,255,255,0.7)', fontWeight:'600', marginTop:1 },

  // Tab bar
  tabBar:         { backgroundColor:'#FFF', borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  tabBarContent:  { paddingHorizontal:8, paddingVertical:6 },
  tabBtn:         { flexDirection:'row', alignItems:'center', paddingHorizontal:14, paddingVertical:8, marginRight:4, gap:5, borderBottomWidth:3, borderBottomColor:'transparent' },
  tabBtnT:        { fontSize:12, color:'#94A3B8', fontWeight:'700' },

  // Loading
  loadWrap:   { flex:1, justifyContent:'center', alignItems:'center', backgroundColor:'#F8FAFC' },
  loadT:      { marginTop:14, color:'#64748B', fontSize:16, fontWeight:'500' },

  // Search
  searchWrap: { flexDirection:'row', alignItems:'center', paddingHorizontal:14, paddingVertical:10, backgroundColor:'#FFF', borderBottomWidth:1, borderBottomColor:'#E2E8F0', gap:8 },
  searchBox:  { flex:1, flexDirection:'row', alignItems:'center', backgroundColor:'#F1F5F9', borderRadius:12, paddingHorizontal:12, paddingVertical:10, gap:8 },
  searchIn:   { flex:1, fontSize:14, color:'#0F172A' },
  filterBtn:  { width:42, height:42, borderRadius:10, backgroundColor:'#EFF6FF', justifyContent:'center', alignItems:'center', borderWidth:1, borderColor:'#BFDBFE' },
  addBtn:     { width:42, height:42, borderRadius:10, backgroundColor:'#0e4e96', justifyContent:'center', alignItems:'center' },

  // List
  list:       { paddingHorizontal:14, paddingTop:12, paddingBottom:80 },

  // Cards
  card:       { backgroundColor:'#FFF', borderRadius:16, borderWidth:1, borderColor:'#E2E8F0', padding:14, marginBottom:12, elevation:2, shadowColor:'#000', shadowOpacity:0.04, shadowRadius:6, shadowOffset:{width:0,height:2} },
  cardRow:    { flexDirection:'row', alignItems:'flex-start', gap:10 },
  cardIcon:   { width:42, height:42, borderRadius:12, justifyContent:'center', alignItems:'center', flexShrink:0 },
  cardTitle:  { fontSize:15, fontWeight:'800', color:'#0F172A' },
  cardSub:    { fontSize:13, color:'#64748B', fontWeight:'600', marginTop:2 },
  cardMeta:   { flexDirection:'row', flexWrap:'wrap', gap:6, marginTop:6 },
  cardActions:{ gap:4 },
  actBtn:     { width:32, height:32, borderRadius:8, backgroundColor:'#F8FAFC', borderWidth:1, borderColor:'#E2E8F0', justifyContent:'center', alignItems:'center' },
  cardDesc:   { color:'#475569', fontSize:13, lineHeight:18, marginTop:8 },
  cardFooter: { flexDirection:'row', alignItems:'center', flexWrap:'wrap', marginTop:10, gap:4 },
  cardFooterT:{ fontSize:12, color:'#94A3B8', marginLeft:2, marginRight:6 },
  cardNote:   { fontSize:12, color:'#94A3B8', fontStyle:'italic', marginTop:4 },
  tagRow:     { flexDirection:'row', flexWrap:'wrap', gap:6, marginTop:8 },
  tag:        { backgroundColor:'#F8FAFC', borderWidth:1, borderColor:'#E2E8F0', borderRadius:20, paddingHorizontal:10, paddingVertical:3 },
  tagT:       { fontSize:11, color:'#64748B', fontWeight:'600' },

  // Badge
  badge:      { backgroundColor:'#F1F5F9', borderRadius:20, paddingHorizontal:8, paddingVertical:3 },
  badgeT:     { fontSize:11, color:'#64748B', fontWeight:'700' },

  // Skill progress
  progRow:    { flexDirection:'row', alignItems:'center', gap:8, marginTop:10 },
  progTrack:  { flex:1, height:6, backgroundColor:'#E5E7EB', borderRadius:3, overflow:'hidden' },
  progFill:   { height:6, borderRadius:3 },
  progLabel:  { fontSize:12, fontWeight:'700', width:34, textAlign:'right' },

  // Empty
  empty:      { alignItems:'center', paddingVertical:40, paddingHorizontal:20 },
  emptyT:     { fontSize:16, fontWeight:'800', color:'#0F172A', marginTop:12 },
  emptyS:     { fontSize:13, color:'#94A3B8', textAlign:'center', marginTop:6, lineHeight:18 },
  emptyBtn:   { marginTop:16, backgroundColor:'#0e4e96', paddingHorizontal:24, paddingVertical:12, borderRadius:12 },
  emptyBtnT:  { color:'#FFF', fontWeight:'700', fontSize:14 },

  // Chips
  chips:      { flexDirection:'row', flexWrap:'wrap', gap:8, marginTop:6, marginBottom:8 },
  chip:       { paddingHorizontal:12, paddingVertical:7, borderRadius:20, backgroundColor:'#F1F5F9', borderWidth:1, borderColor:'#E2E8F0' },
  chipA:      { backgroundColor:'#0e4e96', borderColor:'#0e4e96' },
  chipT:      { fontSize:12, color:'#374151', fontWeight:'500' },
  chipTA:     { color:'#FFF' },

  // Form
  ff:         { marginBottom:14 },
  fl:         { fontSize:13, fontWeight:'700', color:'#0F172A', marginBottom:6 },
  fi:         { borderWidth:1, borderColor:'#E2E8F0', backgroundColor:'#F8FAFC', borderRadius:12, paddingHorizontal:12, paddingVertical:12, fontSize:14, color:'#0F172A' },
  fia:        { minHeight:96, textAlignVertical:'top' },
  fierr:      { borderColor:'#EF4444' },
  ferr:       { fontSize:11, color:'#EF4444', marginTop:3 },

  // Proficiency slider
  profWrap:   { marginTop:8 },
  profTrack:  { height:10, backgroundColor:'#E5E7EB', borderRadius:5, overflow:'hidden', marginBottom:12 },
  profFill:   { height:10, borderRadius:5 },
  profBtns:   { flexDirection:'row', flexWrap:'wrap', gap:6 },
  profBtn:    { paddingHorizontal:12, paddingVertical:6, borderRadius:8, backgroundColor:'#FEF2F2', borderWidth:1, borderColor:'#FECACA' },
  profBtnT:   { fontSize:12, fontWeight:'700', color:'#DC2626' },

  // Modals
  mOverlay:   { flex:1, backgroundColor:'rgba(0,0,0,0.5)', justifyContent:'flex-end' },
  mSheet:     { backgroundColor:'#FFF', borderTopLeftRadius:24, borderTopRightRadius:24, maxHeight:height*0.92, overflow:'hidden' },
  mHdr:       { flexDirection:'row', justifyContent:'space-between', alignItems:'center', padding:18, borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  mTitle:     { fontSize:17, fontWeight:'800', color:'#0F172A' },
  mFooter:    { flexDirection:'row', gap:12, padding:16, borderTopWidth:1, borderTopColor:'#E2E8F0' },
  mGhost:     { flex:1, paddingVertical:13, borderRadius:12, backgroundColor:'#F1F5F9', borderWidth:1, borderColor:'#E2E8F0', alignItems:'center' },
  mGhostT:    { fontSize:14, fontWeight:'700', color:'#374151' },
  mPrimary:   { flex:2, paddingVertical:13, borderRadius:12, backgroundColor:'#0e4e96', alignItems:'center' },
  mPrimaryT:  { fontSize:14, fontWeight:'700', color:'#FFF' },
  saveBtnSm:  { backgroundColor:'#0e4e96', paddingHorizontal:14, paddingVertical:8, borderRadius:10 },
  saveBtnSmT: { color:'#FFF', fontWeight:'700', fontSize:13 },
  fsecT:      { fontSize:13, fontWeight:'700', color:'#0F172A', marginTop:14, marginBottom:6 },

  // Analytics
  periodRow:  { flexDirection:'row', paddingHorizontal:14, paddingVertical:10, gap:8, backgroundColor:'#FFF', borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  periodBtn:  { flex:1, paddingVertical:8, borderRadius:10, backgroundColor:'#F1F5F9', alignItems:'center' },
  periodBtnA: { backgroundColor:'#0e4e96' },
  periodBtnT: { fontSize:12, fontWeight:'700', color:'#64748B' },
  periodBtnTA:{ color:'#FFF' },
  aTabRow:    { flexDirection:'row', paddingHorizontal:14, paddingVertical:8, gap:8, backgroundColor:'#F8FAFC' },
  aTab:       { flex:1, flexDirection:'row', alignItems:'center', justifyContent:'center', paddingVertical:8, borderRadius:10, gap:4 },
  aTabA:      { backgroundColor:'#FFF', elevation:2, shadowColor:'#000', shadowOpacity:0.06, shadowRadius:4 },
  aTabT:      { fontSize:12, fontWeight:'700', color:'#94A3B8' },
  aTabTA:     { color:'#667eea' },
  metricsGrid:{ flexDirection:'row', flexWrap:'wrap', padding:14, gap:10 },
  metricCard: { width:(width-48)/2, borderRadius:16, padding:16, alignItems:'center', gap:4 },
  metricVal:  { fontSize:28, fontWeight:'900', color:'#FFF', marginTop:6 },
  metricLbl:  { fontSize:12, color:'rgba(255,255,255,0.85)', fontWeight:'600' },
  aCard:      { backgroundColor:'#FFF', borderRadius:16, marginHorizontal:14, marginBottom:14, padding:16, borderWidth:1, borderColor:'#E2E8F0' },
  aCardTitle: { fontSize:16, fontWeight:'800', color:'#0F172A', marginBottom:12 },
  summaryRow: { flexDirection:'row', alignItems:'center', marginBottom:10, gap:8 },
  summaryIcon:{ width:34, height:34, borderRadius:10, justifyContent:'center', alignItems:'center' },
  summaryLbl: { width:90, fontSize:13, fontWeight:'600', color:'#374151' },
  summaryBarWrap:{ flex:1 },
  summaryBarTrack:{ height:6, backgroundColor:'#E5E7EB', borderRadius:3, overflow:'hidden' },
  summaryBarFill: { height:6, borderRadius:3 },
  summaryCount:   { width:26, fontSize:14, fontWeight:'800', textAlign:'right' },
  companyRow: { flexDirection:'row', alignItems:'center', paddingVertical:8, borderBottomWidth:1, borderBottomColor:'#F1F5F9', gap:10 },
  companyRank:{ width:26, height:26, borderRadius:13, backgroundColor:'#EFF6FF', justifyContent:'center', alignItems:'center' },
  companyRankT:{ fontSize:12, fontWeight:'800', color:'#0e4e96' },
  companyName:{ flex:1, fontSize:14, fontWeight:'600', color:'#0F172A' },
  companyCount:{ fontSize:13, color:'#94A3B8' },
  distRow:    { flexDirection:'row', alignItems:'center', marginBottom:10, gap:8 },
  distDot:    { width:10, height:10, borderRadius:5 },
  distLbl:    { width:130, fontSize:12, color:'#374151' },
  distBarTrack:{ flex:1, height:6, backgroundColor:'#E5E7EB', borderRadius:3, overflow:'hidden' },
  distBarFill: { height:6, borderRadius:3 },
  distCount:  { width:24, fontSize:13, fontWeight:'800', textAlign:'right' },
  insightItem:{ flexDirection:'row', gap:10, marginBottom:14 },
  insightEmoji:{ fontSize:22 },
  insightTitle:{ fontSize:14, fontWeight:'700', marginBottom:3 },
  insightText: { fontSize:13, lineHeight:18 },
  recRow:     { flexDirection:'row', alignItems:'center', paddingVertical:12, borderBottomWidth:1, borderBottomColor:'#F1F5F9', gap:10 },
  recIcon:    { width:38, height:38, borderRadius:10, justifyContent:'center', alignItems:'center' },
  recTitle:   { fontSize:14, fontWeight:'700', color:'#0F172A' },
  recText:    { fontSize:12, color:'#94A3B8', marginTop:2 },
});
